<template>
    <div class="reseller-order-result">
        <!-- Card-Flow: solange noch keine paid-Bestätigung vorliegt → "in Abwicklung" -->
        <template v-if="!ready">
            <div class="uk-alert-primary" uk-alert>
                <h3 class="uk-margin-remove">{{ $t('Bestellung in Abwicklung') }}</h3>
                <p class="uk-margin-remove-bottom">
                    {{ $t('Bestellnummer') }}:
                    <a v-if="orderBackendUrl" :href="orderBackendUrl" target="_blank" rel="noopener">
                        <strong>{{ orderNumber }}</strong>
                    </a>
                    <strong v-else>{{ orderNumber }}</strong>
                </p>
                <p v-if="reseller && reseller.fiskaly_signature_counter" class="uk-text-meta uk-margin-remove-top">
                    TSE #{{ reseller.fiskaly_signature_counter }}
                </p>
            </div>

            <div class="uk-flex uk-flex-middle uk-flex-column uk-margin-medium-top uk-text-center">
                <img :src="iconUrl"
                     :alt="isCardFlow ? $t('Warte auf Zahlung') : $t('Bestellung wird verarbeitet')"
                     style="width:96px;height:96px"/>
                <h4 class="uk-margin-medium-top uk-margin-remove-bottom">
                    {{ isCardFlow ? $t('Warte auf Zahlung') : $t('Bestellung wird verarbeitet') }}
                </h4>
                <p class="uk-text-meta uk-margin-small-top">
                    {{ isCardFlow
                        ? $t('Bitte Karte am Terminal halten. Diese Seite aktualisiert sich automatisch.')
                        : $t('Die Bestellung wird gerade verarbeitet, bitte einen Moment warten.') }}
                </p>
            </div>

            <div v-if="testCheckoutUrl" class="uk-margin-medium-top uk-text-center">
                <button class="uk-button uk-button-default uk-button-small"
                        type="button"
                        @click="openMollieTestCheckout">
                    {{ $t('Test-Bezahlung im Mollie-Dashboard öffnen') }}
                </button>
            </div>

            <div class="uk-margin-medium-top uk-flex uk-flex-right">
                <button class="uk-button uk-button-danger uk-button-small" @click="cancel">
                    {{ $t('Bestellung abbrechen') }}
                </button>
            </div>
        </template>

        <!-- Order ist paid bzw. Cash-Flow → "erfolgreich" + Drucken -->
        <template v-else>
            <div class="uk-alert-success" uk-alert>
                <h3 class="uk-margin-remove">{{ $t('Bestellung erfolgreich') }}</h3>
                <div class="uk-flex uk-flex-middle uk-flex-between uk-flex-wrap uk-margin-remove-bottom">
                    <p class="uk-margin-remove">
                        {{ $t('Bestellnummer') }}:
                        <a v-if="orderBackendUrl" :href="orderBackendUrl" target="_blank" rel="noopener">
                            <strong>{{ orderNumber }}</strong>
                        </a>
                        <strong v-else>{{ orderNumber }}</strong>
                    </p>
                    <a v-if="invoiceUrl"
                       :href="invoiceUrl"
                       target="_blank"
                       rel="noopener"
                       class="uk-button uk-button-default uk-button-small">
                        {{ $t('Rechnung herunterladen') }}
                    </a>
                </div>
                <p v-if="reseller && reseller.fiskaly_signature_counter && !reseller.fiskaly_offline" class="uk-text-meta uk-margin-remove-top">
                    TSE #{{ reseller.fiskaly_signature_counter }}
                </p>
            </div>

            <!--
              Cashless: keine eigene TSE-Signatur und bewusst keine TSE-Warnung.
              Der Umsatz wird beim Cashless-Anbieter fiskalisiert, der Beleg
              kommt von dessen Terminal.
            -->
            <div v-if="reseller && reseller.external_provider" class="uk-alert-primary" uk-alert>
                <p class="uk-margin-remove">
                    {{ $t('Cashless-Zahlung — der Beleg kommt vom Terminal des Cashless-Anbieters. festiware erstellt für diesen Verkauf keinen eigenen Kassenbeleg.') }}
                </p>
            </div>

            <div v-if="reseller && reseller.fiskaly_offline" class="uk-alert-warning" uk-alert>
                <p class="uk-margin-remove">
                    <strong>{{ $t('TSE aktuell offline') }}.</strong>
                    {{ $t('Verkauf erfasst und Ticket gültig — die Fiskaly-Signatur wird automatisch nachgeholt, sobald die TSE wieder erreichbar ist. Bitte das Problem zeitnah von der Administration beheben lassen (§146a AO).') }}
                </p>
            </div>

            <h4 class="uk-margin-medium-top">{{ $t('Tickets drucken') }}</h4>
            <p v-if="!hasAnyPrintUrl" class="uk-text-muted">
                {{ $t('Es wurden keine Ticket-PDFs erzeugt.') }}
            </p>

            <!--
              Druckziel-Wähler. Default = Download (heutiges Verhalten). Wenn
              Drucker zugewiesen sind, kann der Kassierer einen davon auswählen
              und die Wahl bleibt per localStorage über Bestellungen erhalten.
              Bei print_mode='both' gibt es zwei Zeilen (PDF + Label), sonst nur
              die zum aktiven Format.
            -->
            <div v-if="hasAnyPrintUrl && printers.length" class="uk-margin-small-bottom uk-grid-small" uk-grid>
                <div v-if="showPdf" class="uk-width-1-1 uk-width-1-2@s">
                    <label class="uk-form-label uk-text-small">{{ $t('PDF drucken über') }}</label>
                    <select class="uk-select uk-form-small"
                            :value="selectedPdfTargetId"
                            @change="onPdfTargetChange($event.target.value)">
                        <option value="download">{{ $t('Download (in neuem Tab öffnen)') }}</option>
                        <option v-for="p in printers" :key="`pdf-${p.id}`" :value="String(p.id)">
                            {{ p.name }}
                        </option>
                    </select>
                </div>
                <div v-if="showLabel" class="uk-width-1-1 uk-width-1-2@s">
                    <label class="uk-form-label uk-text-small">{{ $t('Label drucken über') }}</label>
                    <select class="uk-select uk-form-small"
                            :value="selectedLabelTargetId"
                            @change="onLabelTargetChange($event.target.value)">
                        <option value="download">{{ $t('Download (in neuem Tab öffnen)') }}</option>
                        <option v-for="p in printers" :key="`label-${p.id}`" :value="String(p.id)">
                            {{ p.name }}
                        </option>
                    </select>
                </div>
            </div>

            <ul v-if="hasAnyPrintUrl" class="uk-list uk-list-divider">
                <li v-for="(_, index) in pdfUrls" :key="`row-${index}`" class="uk-flex uk-flex-middle uk-flex-between uk-flex-wrap">
                    <div class="uk-margin-small-right">
                        <strong>{{ $t('Ticket') }} {{ index + 1 }}</strong>
                        <span v-if="printJobStatus(`pdf-${index}`)" class="uk-text-meta uk-margin-small-left">
                            {{ printJobStatus(`pdf-${index}`) }}
                        </span>
                    </div>
                    <div>
                        <button v-if="showPdf && pdfUrls[index]"
                                type="button"
                                :disabled="printJobBusy(`pdf-${index}`)"
                                class="uk-button uk-button-primary uk-button-small uk-margin-small-right"
                                @click="handlePrint('pdf', index, pdfUrls[index])">
                            {{ pdfButtonLabel }}
                        </button>
                        <button v-if="showLabel && labelUrls[index]"
                                type="button"
                                :disabled="printJobBusy(`label-${index}`)"
                                class="uk-button uk-button-secondary uk-button-small"
                                @click="handlePrint('label', index, labelUrls[index])">
                            {{ labelButtonLabel }}
                        </button>
                    </div>
                </li>
            </ul>

            <div class="uk-margin-medium-top uk-flex uk-flex-right">
                <button class="uk-button uk-button-default" @click="newOrder">
                    {{ $t('Neue Bestellung') }}
                </button>
            </div>
        </template>
    </div>
</template>

<script>
import helper from "../../helper/helper";

const POLL_INTERVAL_MS = 5000;

export default {
    mixins: [helper],
    props: {
        orderNumber: { type: String, required: true },
        reseller:    { type: Object, default: () => ({}) },
    },
    data() {
        return {
            // local mutable copy — initial value comes from prop, polling overwrites
            currentReseller: this.reseller || {},
            isPaid: this.computeIsPaid(this.reseller),
            pollHandle: null,
            mollieModalOpened: false,
            // Print destinations
            printers: [],
            selectedPdfTargetId: 'download',     // 'download' or stringified supply_item id
            selectedLabelTargetId: 'download',
            // Per-row job state — key = 'pdf-0' / 'label-2'; value = 'busy' | 'done' | 'error'
            printJobs: {},
        };
    },
    computed: {
        ready() {
            // @gotcha Cash-Flow: ticket_codes werden asynchron via Queue erzeugt
            // (TicketCodeCreateFromOrderPositions-Job). Die Initial-Response liefert
            // bei offline_pending/signed sofort isPaid=true, aber ticket_pdf_urls=[].
            // Ohne hasAnyPrintUrl-Gate würde mounted() kein Polling starten und die
            // Druck-Buttons blieben für immer leer.
            return this.isPaid && this.hasAnyPrintUrl;
        },
        isCardFlow() {
            // Card-Flow ist eindeutig an status='awaiting_customer_tap' erkennbar.
            // Alles andere im Pending-State (signed/offline_pending ohne URLs) ist Cash.
            return this.currentReseller && this.currentReseller.status === 'awaiting_customer_tap';
        },
        printMode() {
            return (this.currentReseller && this.currentReseller.print_mode) || 'pdf_only';
        },
        showPdf() {
            return this.printMode === 'pdf_only' || this.printMode === 'both';
        },
        showLabel() {
            return this.printMode === 'label_only' || this.printMode === 'both';
        },
        pdfUrls() {
            return (this.currentReseller && this.currentReseller.ticket_pdf_urls) || [];
        },
        labelUrls() {
            return (this.currentReseller && this.currentReseller.ticket_label_urls) || [];
        },
        hasAnyPrintUrl() {
            return (this.showPdf && this.pdfUrls.length > 0)
                || (this.showLabel && this.labelUrls.length > 0);
        },
        iconUrl() {
            // SVG bringt eigene CSS-Animation mit (Rotation + Rainbow-Cycle).
            // Inline-styled <img> umgeht den toten ticketshop.css-Pfad — die
            // Animation läuft, sobald das SVG geladen ist, ohne externes CSS.
            return '/plugins/kosmoskosmos/festiware/assets/images/festiware_loading-spinner_v1.svg';
        },
        testCheckoutUrl() {
            // Test-Modus: Mollie-Checkout-URL wird mitgeliefert, damit der
            // Kassierer den Tap im Sandbox-Dashboard auslösen kann.
            if (!this.currentReseller || !this.currentReseller.mollie_testmode) return '';
            return this.currentReseller.mollie_checkout_url || '';
        },
        orderBackendUrl() {
            return this.currentReseller && this.currentReseller.order_backend_url
                ? this.currentReseller.order_backend_url
                : '';
        },
        invoiceUrl() {
            return (this.currentReseller && this.currentReseller.invoice_url) || '';
        },
        pdfButtonLabel() {
            return this.selectedPdfTargetId === 'download'
                ? this.$t('PDF herunterladen')
                : this.$t('PDF drucken');
        },
        labelButtonLabel() {
            return this.selectedLabelTargetId === 'download'
                ? this.$t('Label herunterladen')
                : this.$t('Label drucken');
        },
    },
    mounted() {
        // Test-Modus: Mollie-Checkout-Modal automatisch beim ersten Render
        // öffnen — der Kassierer muss dort die Sandbox-Zahlung bestätigen,
        // damit der Webhook auslöst und das Polling auf paid umschwenkt.
        if (this.testCheckoutUrl && !this.mollieModalOpened) {
            this.openMollieTestCheckout();
        }
        if (!this.ready) {
            this.startPolling();
        }
        // Restore choice + load available printers (one shot, cached server-side)
        this.restorePrintTargets();
        this.fetchPrinters();
    },
    beforeUnmount() {
        this.stopPolling();
    },
    beforeDestroy() {
        this.stopPolling();
    },
    methods: {
        computeIsPaid(reseller) {
            if (!reseller) return false;
            // Cash-Flow: kein status-Feld, aber sofort signed → paid.
            // Card-Flow: explizit status === 'awaiting_customer_tap' bis Webhook paid liefert.
            if (reseller.status === 'awaiting_customer_tap') return false;
            if (reseller.status === 'paid') return true;
            // Cash: signed (oder offline_pending) + ticket urls bedeutet paid.
            const hasUrls = (reseller.ticket_pdf_urls && reseller.ticket_pdf_urls.length > 0)
                || (reseller.ticket_label_urls && reseller.ticket_label_urls.length > 0);
            return hasUrls || reseller.fiskaly_state === 'signed' || reseller.fiskaly_state === 'offline_pending';
        },
        startPolling() {
            this.stopPolling();
            this.pollHandle = setInterval(() => this.pollOnce(), POLL_INTERVAL_MS);
        },
        stopPolling() {
            if (this.pollHandle) {
                clearInterval(this.pollHandle);
                this.pollHandle = null;
            }
        },
        pollOnce() {
            this.doOctoberRequest('onPollResellerOrderStatus', { order_number: this.orderNumber })
                .then((response) => {
                    if (!response || !response.reseller) return;
                    this.currentReseller = response.reseller;
                    if (response.is_paid || this.computeIsPaid(response.reseller)) {
                        this.isPaid = true;
                    }
                    // Stop only once URLs have actually materialized — Cash-Flow
                    // reports isPaid before the ticket_codes Job has finished.
                    if (this.ready) {
                        this.stopPolling();
                    }
                })
                .catch(() => {
                    // Single failure = transient (Webhook-Roundtrip läuft noch).
                    // Polling läuft weiter; bei dauerhafter Misskonfig sieht der
                    // Kassierer das im Network-Tab — UI-Seitig ohne Toast bleiben,
                    // sonst wird's ein Spam-Karussell.
                });
        },
        openMollieTestCheckout() {
            this.mollieModalOpened = true;
            const url = this.testCheckoutUrl;
            if (!url) return;
            // 1. Versuch: neues Tab/Fenster
            const win = window.open(url, '_blank', 'noopener');
            if (win) return;
            // 2. Fallback: UIkit-Modal mit klickbarem Link
            if (typeof UIkit !== 'undefined' && UIkit.modal && UIkit.modal.dialog) {
                UIkit.modal.dialog(
                    `<div class="uk-modal-body">
                        <h3>${this.$t('Test-Zahlung Mollie')}</h3>
                        <p>${this.$t('Pop-up wurde blockiert. Bitte den folgenden Link öffnen:')}</p>
                        <p><a href="${url}" target="_blank" rel="noopener">${url}</a></p>
                        <p class="uk-text-meta">
                            ${this.$t('Alternativ im Mollie-Dashboard unter "Umsatz" die offene Zahlung manuell auf bezahlt setzen.')}
                        </p>
                    </div>
                    <div class="uk-modal-footer uk-text-right">
                        <button class="uk-button uk-button-default uk-modal-close" type="button">
                            ${this.$t('Schließen')}
                        </button>
                    </div>`
                );
            } else {
                // 3. Letzter Fallback: window.location
                window.location.href = url;
            }
        },
        cancel() {
            this.stopPolling();
            // Order wird über den existierenden Cleanup-Cron aufgeräumt
            // (started → cancel nach Grace-Period; offline_pending → bleibt für
            // späteren Retry, falls Mollie doch noch paid liefert).
            this.$emit('new-order');
        },
        newOrder() {
            this.$emit('new-order');
        },

        // ── Print target selection ──────────────────────────────────────────

        /**
         * One key per browser+host; the cashier's machine maps 1:1 to one
         * instance, so we don't need a per-tenant scope beyond the hostname.
         */
        lsKey(format) {
            return `festiware.reseller.print_target.${window.location.hostname}.${format}`;
        },

        restorePrintTargets() {
            try {
                this.selectedPdfTargetId   = localStorage.getItem(this.lsKey('pdf'))   || 'download';
                this.selectedLabelTargetId = localStorage.getItem(this.lsKey('label')) || 'download';
            } catch (e) { /* localStorage disabled — stay at defaults */ }
        },

        fetchPrinters() {
            this.doOctoberRequest('onGetResellerPrinters', {})
                .then((response) => {
                    const list = (response && response.printers) || [];
                    this.printers = list;
                    // First load: if user has no saved choice and there is a
                    // default printer, pre-select it (still saved on first
                    // explicit change — we don't write back here so the user
                    // can fall back to Download by leaving the page alone).
                    if (this.selectedPdfTargetId === 'download') {
                        const def = list.find(p => p.is_default);
                        if (def) this.selectedPdfTargetId = String(def.id);
                    }
                    if (this.selectedLabelTargetId === 'download') {
                        const def = list.find(p => p.is_default);
                        if (def) this.selectedLabelTargetId = String(def.id);
                    }
                })
                .catch(() => { this.printers = []; });
        },

        onPdfTargetChange(value) {
            this.selectedPdfTargetId = value;
            try { localStorage.setItem(this.lsKey('pdf'), value); } catch (e) { /* ignore */ }
        },

        onLabelTargetChange(value) {
            this.selectedLabelTargetId = value;
            try { localStorage.setItem(this.lsKey('label'), value); } catch (e) { /* ignore */ }
        },

        printJobBusy(key) {
            return this.printJobs[key] === 'busy';
        },

        printJobStatus(key) {
            switch (this.printJobs[key]) {
                case 'busy':  return this.$t('… wird gedruckt');
                case 'done':  return this.$t('✓ gedruckt');
                case 'error': return this.$t('Fehler — bitte erneut versuchen');
                default:      return '';
            }
        },

        /**
         * Either open the asset URL (legacy Download flow) or hand it off
         * to the festiware.app print queue. The button stays per-ticket so
         * the cashier can re-print a single ticket without re-printing all.
         */
        handlePrint(format, index, url) {
            const target = format === 'pdf' ? this.selectedPdfTargetId : this.selectedLabelTargetId;
            const key = `${format}-${index}`;

            if (target === 'download' || !target) {
                window.open(url, '_blank', 'noopener');
                return;
            }

            this.printJobs = { ...this.printJobs, [key]: 'busy' };
            this.doOctoberRequest('onSendResellerPrintJob', {
                url:            url,
                supply_item_id: target,
                context_ref:    `order:${this.orderNumber}#${format}-${index + 1}`,
            })
                .then(() => {
                    this.printJobs = { ...this.printJobs, [key]: 'done' };
                })
                .catch(() => {
                    this.printJobs = { ...this.printJobs, [key]: 'error' };
                });
        },
    },
};
</script>

